import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Loader2,
  Maximize2Icon,
} from "lucide-react";
import { useMemo, useState } from "react";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { imagePreviewerMessages } from "@/components/imagePreview/imagePreviewer.intl";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/HoverCard";
import { Skeleton } from "@/components/ui/skeleton";
import { preventImagePreviewContextMenu } from "@/utils/content/imageHelper";
import { cn } from "@/utils/core/generalUtils";

type ImageLoadStatus = "loading" | "loaded" | "error";

interface PreviewableImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  skeletonClassName?: string;
  showErrorText?: boolean;
}

const PreviewableImage = (props: PreviewableImageProps): React.JSX.Element => {
  const {
    src,
    alt,
    className,
    containerClassName,
    skeletonClassName,
    showErrorText = false,
  } = props;
  const tImagePreviewer = useIntlMessages(imagePreviewerMessages);
  const [loadStatus, setLoadStatus] = useState<ImageLoadStatus>("loading");
  const [prevSrc, setPrevSrc] = useState(src);

  if (src !== prevSrc) {
    setPrevSrc(src);
    setLoadStatus("loading");
  }

  const isLoading = loadStatus === "loading";
  const hasError = loadStatus === "error";

  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-hidden",
        containerClassName ?? className,
      )}
      onContextMenu={preventImagePreviewContextMenu}
    >
      {isLoading && (
        <>
          <Skeleton
            className={cn("absolute inset-0 size-full", skeletonClassName)}
            aria-hidden
          />
          <div
            className='absolute inset-0 flex items-center justify-center'
            role='status'
          >
            <Loader2 className='size-5 animate-spin text-blue-700' />
          </div>
        </>
      )}
      {hasError && (
        <div
          className={cn(
            "absolute inset-0 flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground",
            skeletonClassName,
          )}
          role='img'
          aria-label={tImagePreviewer("imageLoadFailed")}
        >
          <ImageOff className='size-5 shrink-0' aria-hidden />
          {showErrorText && (
            <span className='px-2 text-center text-xs'>
              {tImagePreviewer("imageLoadFailed")}
            </span>
          )}
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={cn(
          "max-h-full max-w-full",
          className,
          (isLoading || hasError) && "invisible",
        )}
        onLoad={() => setLoadStatus("loaded")}
        onError={() => setLoadStatus("error")}
      />
    </div>
  );
};

interface ImagePreviewerProps {
  showNameTitle?: boolean;
  imagesSrc: string | string[];
  alt?: string;
  classes?: {
    imgClassName?: string;
    containerClassName?: string;
  };
}

interface ImagePreviewerPropsWithTrigger {
  trigger: JSX.Element;
  imageCount?: never;
}

interface ImagePreviewerPropsWithoutTrigger {
  trigger?: never;
  imageCount: number;
}

interface ImagePreviewerPropsWithHoverContent {
  showDialogOnly?: never;
  showHoverContent?: boolean;
  side?: "left" | "right" | "top" | "bottom";
}

interface ImagePreviewerPropsDialogOnly {
  showDialogOnly: boolean;
  showHoverContent?: never;
  side?: never;
}

export const ImagePreviewer: React.FC<
  ImagePreviewerProps &
    (ImagePreviewerPropsDialogOnly | ImagePreviewerPropsWithHoverContent) &
    (ImagePreviewerPropsWithTrigger | ImagePreviewerPropsWithoutTrigger)
> = (props) => {
  const {
    trigger,
    imagesSrc,
    alt = "Image",
    imageCount = 4,
    showHoverContent = false,
    classes,
    showDialogOnly = !showHoverContent,
    side = "bottom",
    showNameTitle = false,
  } = props;
  const tImagePreviewer = useIntlMessages(imagePreviewerMessages);

  const srcArray = useMemo(
    () => (Array.isArray(imagesSrc) ? imagesSrc : [imagesSrc]).filter(Boolean),
    [imagesSrc],
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevSrcArray, setPrevSrcArray] = useState(srcArray);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (srcArray !== prevSrcArray) {
    setPrevSrcArray(srcArray);
    setCurrentIndex(0);
  }

  if (!srcArray.length) {
    return null;
  }

  const currentSrc = srcArray[currentIndex] ?? srcArray[0];
  const hasMultipleImages = srcArray.length > 1;
  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === srcArray.length - 1;

  const goToPreviousImage = (): void => {
    if (!isFirstImage) {
      setCurrentIndex((index) => index - 1);
    }
  };

  const goToNextImage = (): void => {
    if (!isLastImage) {
      setCurrentIndex((index) => index + 1);
    }
  };

  const handleDialogKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (event.key === "ArrowLeft" && !isFirstImage) {
      event.preventDefault();
      goToPreviousImage();
      return;
    }

    if (event.key === "ArrowRight" && !isLastImage) {
      event.preventDefault();
      goToNextImage();
    }
  };

  const thumbnailNodes = srcArray.slice(0, imageCount).map((image, index) => (
    <button
      key={`${image}-${index}`}
      type='button'
      onMouseOver={() => setCurrentIndex(index)}
      onClick={() => {
        setCurrentIndex(index);
        setIsDialogOpen(true);
      }}
      className={cn("rounded-md", classes?.containerClassName)}
    >
      <PreviewableImage
        src={image}
        alt={`${alt} ${index + 1}`}
        className={cn("size-14 rounded-sm object-cover", classes?.imgClassName)}
        skeletonClassName={cn("size-14 rounded-sm", classes?.imgClassName)}
      />
    </button>
  ));

  const previewBody = (
    <div className={cn("flex gap-4 flex-wrap", classes?.containerClassName)}>
      {thumbnailNodes}
      {srcArray.length > imageCount && (
        <button
          type='button'
          onClick={() => {
            setCurrentIndex(imageCount);
            setIsDialogOpen(true);
          }}
          className={cn(
            "flex size-14 items-center justify-center rounded-sm bg-gray-200 text-xs text-gray-600",
            classes?.imgClassName,
          )}
        >
          +{srcArray.length - imageCount}
        </button>
      )}
    </div>
  );

  return (
    <>
      <HoverCard>
        {trigger ? (
          <HoverCardTrigger
            asChild
            onContextMenu={preventImagePreviewContextMenu}
            onClick={() => {
              if (showDialogOnly) {
                setIsDialogOpen(true);
              }
            }}
          >
            {trigger}
          </HoverCardTrigger>
        ) : (
          previewBody
        )}

        {showHoverContent && !showDialogOnly && (
          <HoverCardContent side={side} className='w-64'>
            <div className='relative'>
              <PreviewableImage
                src={currentSrc}
                alt={alt}
                containerClassName='h-52 w-full max-w-52'
                className='size-full object-cover'
                skeletonClassName='size-full'
              />
              <button
                type='button'
                onClick={() => setIsDialogOpen(true)}
                className='absolute right-2 top-2 rounded bg-background/80 p-1'
              >
                <Maximize2Icon className='size-4' />
              </button>
            </div>
          </HoverCardContent>
        )}
      </HoverCard>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className='max-h-[95vh] max-w-[95vw] sm:max-w-5xl'
          onKeyDown={handleDialogKeyDown}
        >
          <DialogTitle>
            <VisuallyHidden>{alt}</VisuallyHidden>
          </DialogTitle>
          <DialogDescription>
            <VisuallyHidden>
              {showNameTitle ? currentSrc : `Previewing ${alt}`}
            </VisuallyHidden>
          </DialogDescription>
          <div className='relative flex min-h-[40vh] w-full max-h-[80vh] items-center justify-center px-10 sm:px-12'>
            {hasMultipleImages && (
              <Button
                type='button'
                variant='outline'
                size='icon'
                className='absolute left-0 top-1/2 z-10 -translate-y-1/2'
                onClick={goToPreviousImage}
                disabled={isFirstImage}
                aria-label={tImagePreviewer("previousImage")}
              >
                <ChevronLeft className='size-5' />
              </Button>
            )}
            <PreviewableImage
              src={currentSrc}
              alt={alt}
              containerClassName='h-full min-h-[40vh] w-full max-h-[80vh]'
              className='h-auto max-h-[80vh] w-auto object-contain'
              skeletonClassName='h-full min-h-[40vh] w-full max-w-full'
              showErrorText
            />
            {hasMultipleImages && (
              <Button
                type='button'
                variant='outline'
                size='icon'
                className='absolute right-0 top-1/2 z-10 -translate-y-1/2'
                onClick={goToNextImage}
                disabled={isLastImage}
                aria-label={tImagePreviewer("nextImage")}
              >
                <ChevronRight className='size-5' />
              </Button>
            )}
          </div>
          {hasMultipleImages && (
            <p className='text-center text-base text-muted-foreground'>
              {currentIndex + 1} / {srcArray.length}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
